import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'
import type { ConsigneeInventory } from '../lib/database.types'
import { SALES_KEY } from './useSales'
import { CONSIGNEES_KEY } from './useConsignees'

export const CONSIGNEE_INVENTORY_KEY = ['consignee_inventory']

/** All inventory rows for one consignee, joined to nothing — caller maps to products. */
export function useConsigneeInventory(consigneeId: string | undefined) {
  return useQuery({
    queryKey: [...CONSIGNEE_INVENTORY_KEY, consigneeId],
    enabled: !!consigneeId,
    queryFn: async (): Promise<ConsigneeInventory[]> => {
      const { data, error } = await supabase
        .from('consignee_inventory')
        .select('*')
        .eq('consignee_id', consigneeId!)
      if (error) throw error
      return data ?? []
    },
  })
}

/**
 * All inventory rows across every consignee owned by this user. Used for
 * Mom Mode (only one consignee row visible per RLS, so this returns just
 * mom's inventory) AND owner side when we need a single fetch.
 */
export function useAllConsigneeInventory() {
  return useQuery({
    queryKey: CONSIGNEE_INVENTORY_KEY,
    queryFn: async (): Promise<ConsigneeInventory[]> => {
      const { data, error } = await supabase
        .from('consignee_inventory')
        .select('*')
      if (error) throw error
      return data ?? []
    },
  })
}

/**
 * Owner hands off (or takes back) inventory. Passes signed quantity to
 * the Postgres RPC which upserts + writes the handoff audit row atomically.
 */
export function useHandoffProducts() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: {
      consignee_id: string
      product_id: string
      quantity: number
      note?: string | null
    }): Promise<void> => {
      const { error } = await supabase.rpc('increment_consignee_inventory', {
        p_consignee_id: input.consignee_id,
        p_product_id: input.product_id,
        p_quantity: input.quantity,
        p_note: input.note ?? null,
      })
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CONSIGNEE_INVENTORY_KEY })
      queryClient.invalidateQueries({ queryKey: ['consignee_handoffs'] })
    },
  })
}

/**
 * Consignee logs a sale via the SECURITY DEFINER RPC. Validates
 * identity, checks stock, inserts sale + line item, decrements inventory.
 */
export function useLogConsigneeSale() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: {
      consignee_id: string
      product_id: string
      sale_price: number
      quantity?: number
      note?: string | null
    }): Promise<string> => {
      const { data, error } = await supabase.rpc('log_consignee_sale', {
        p_consignee_id: input.consignee_id,
        p_product_id: input.product_id,
        p_sale_price: input.sale_price,
        p_quantity: input.quantity ?? 1,
        p_note: input.note ?? null,
      })
      if (error) throw error
      return data as string
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CONSIGNEE_INVENTORY_KEY })
      queryClient.invalidateQueries({ queryKey: SALES_KEY })
      queryClient.invalidateQueries({ queryKey: CONSIGNEES_KEY })
      queryClient.invalidateQueries({ queryKey: ['products'] })
    },
  })
}
